import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { EmptyState } from "@/components/shared/EmptyState";
import { useOpsPipeline } from "@/api/hooks";
import { Workflow, AlertTriangle } from "lucide-react";

// Items 2/3 (Aug 2026 Operations round) — replaces the old "Work Queue" nav
// tab (still mounted at /operations/tasks for direct URL access, per the
// same pattern used for the earlier Finance nav consolidation) with a real
// Operations Pipeline: every Job categorized by its CURRENT Operations
// stage. Mirrors the Column/Card visual language of
// frontend/src/pages/finance/Pipeline.jsx exactly, but the stage bucketing
// itself is Operations-specific — computed server-side by GET
// /operations/pipeline (server.py, _ops_pipeline_stage) purely from the
// Job's existing status + execution milestones + item-9 planning checklist
// + item-10 handoff flag. No new status field, no duplicate workflow.
const STAGE_TONE = {
  "New Job / Handoff Received": "default", "Documentation Pending": "warn",
  "Booking & Planning": "default", "Pickup / Origin Handling": "default",
  "In Transit": "default", "Arrival / Destination Handling": "default",
  "Customs / Clearance": "warn", "Delivery": "default", "POD Pending": "warn",
  "Cost / Invoice Documentation Pending": "warn", "Ready for Finance": "success", "Completed": "success",
};

function Column({ title, count, children, empty }) {
  return (
    <div className="flex flex-col w-[320px] shrink-0">
      <div className="flex items-center justify-between px-1 mb-2">
        <span className="font-display font-semibold text-sm text-slate-700">{title}</span>
        <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-full px-1.5">{count}</span>
      </div>
      <div className="flex-1 space-y-2">{count === 0 ? <div className="text-[12px] text-slate-400 px-1 py-3">{empty}</div> : children}</div>
    </div>
  );
}

export default function OpsPipeline() {
  const navigate = useNavigate();
  const { data, isLoading } = useOpsPipeline();
  if (isLoading || !data) return <Loading />;

  const total = Object.values(data.columns).reduce((n, rows) => n + rows.length, 0);

  return (
    <>
      <PageHeader title="Operations pipeline" subtitle="Every Job in execution, grouped by its current Operations stage" />
      <div className="flex-1 overflow-auto p-6">
        {total === 0 ? (
          <EmptyState icon={Workflow} title="No Jobs in the Operations pipeline" sub="Jobs will appear here once Finance hands off accepted orders to Operations." />
        ) : (
          <div className="flex gap-4 min-w-max">
            {data.stages.map((stage) => {
              const rows = data.columns[stage] || [];
              return (
                <Column key={stage} title={stage} count={rows.length} empty="Nothing at this stage.">
                  {rows.map((row) => (
                    <button
                      key={row.jobId}
                      data-testid={`ops-pipeline-${row.jobId}`}
                      onClick={() => navigate(`/operations/jobs/${row.jobId}`)}
                      className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-indigo-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-sm text-slate-800 truncate">{row.jobId}</span>
                        <StatusPill status={stage} bucket={STAGE_TONE[stage]} />
                      </div>
                      <div className="text-[12px] text-slate-600 mb-0.5 truncate">{row.customer}</div>
                      <div className="text-[12px] text-slate-500 mb-1 truncate">{row.lane} · {row.mode || "—"}</div>
                      {row.nextAction && <div className="text-[11px] font-medium text-indigo-600 mb-1">{row.nextAction}</div>}
                      {(row.etd || row.eta) && (
                        <div className="text-[11px] text-slate-500 font-mono-num mb-1">
                          {row.etd ? `ETD ${String(row.etd).slice(0, 16).replace("T", " ")}` : ""}{row.etd && row.eta ? " · " : ""}{row.eta ? `ETA ${String(row.eta).slice(0, 16).replace("T", " ")}` : ""}
                        </div>
                      )}
                      {row.blocker && (
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                          <AlertTriangle className="h-3 w-3 shrink-0" /><span className="truncate">{row.blocker}</span>
                        </div>
                      )}
                      <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-400">
                        <span>{row.assignedOpsUser || "Unassigned"}</span>
                        <span className="font-mono-num">{(row.dateAssigned || "").slice(0, 10) || "—"}</span>
                      </div>
                    </button>
                  ))}
                </Column>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
